import { Routes } from '@angular/router';
import { isAuth } from './core/guards/oidc.auth.guard';
import { authCallbackGuard } from './core/guards/auth-callback.guard';
import { PageLayoutComponent } from './shared/layouts/page-layout/page-layout.component';
import { canonicalProductCardMatcher } from './product-card-route.matcher';

export const routes: Routes = [
  {
    path: 'authorization',
    canActivate: [authCallbackGuard],
    loadComponent: () =>
      import('./pages/auth-callback/auth-callback.component').then(
        ({ AuthCallbackComponent }) => AuthCallbackComponent,
      ),
  },
  {
    path: '',
    component: PageLayoutComponent,
    children: [
      {
        path: 'orders',
        canActivate: [isAuth],
        data: {
          seo: {
            title: 'Мои заявки',
            description: 'Список ваших заявок Рефорс',
            noindex: true,
          },
        },
        loadComponent: () =>
          import('./pages/orders/order-list/order-list.component').then(
            ({ OrderListComponent }) => OrderListComponent,
          ),
      },
      {
        path: 'orders/order/:id',
        canActivate: [isAuth],
        data: {
          seo: { title: 'Заказ', description: 'Детали заказа Рефорс', noindex: true },
        },
        loadComponent: () =>
          import('./pages/local-order/local-order.component').then(
            ({ LocalOrderComponent }) => LocalOrderComponent,
          ),
      },
      {
        path: 'local-orders/:id',
        redirectTo: 'orders/order/:id',
      },
      {
        path: '',
        pathMatch: 'full',
        data: {
          seo: {
            title: 'Охранные системы и сервисы безопасности',
            description:
              'Рефорс — охрана квартир, домов и бизнеса под ключ: датчики, монтаж и круглосуточный пульт реагирования. Выберите готовый пакет безопасности и подключитесь за 24 часа.',
            image: 'assets/images/backgrounds/main.jpg',
          },
        },
        loadComponent: () =>
          import('./pages/main/main.component').then(({ MainComponent }) => MainComponent),
      },
      { path: 'main', redirectTo: '', pathMatch: 'full' },
      {
        path: 'configurator',
        data: {
          seo: {
            title: 'Подбор охранной системы',
            description:
              'Ответьте на несколько вопросов и получите подходящую комплектацию охраны.',
          },
        },
        loadComponent: () =>
          import('./pages/configurator/configurator.component').then(
            ({ ConfiguratorComponent }) => ConfiguratorComponent,
          ),
      },
      {
        path: 'dom',
        data: {
          seo: {
            title: 'Охрана квартиры, дома и дачи',
            description:
              'Умная охрана дома Рефорс: датчики, сигнализация и реагирование 24/7. Бесплатный выезд и монтаж, управление со смартфона. Готовые пакеты для квартиры, коттеджа и дачи.',
            image: 'assets/images/backgrounds/for-home.jpg',
          },
        },
        loadComponent: () =>
          import('./pages/for-home/for-home.component').then(
            ({ ForHomeComponent }) => ForHomeComponent,
          ),
      },
      {
        path: 'biznes',
        data: {
          seo: {
            title: 'Охрана бизнеса и коммерческих объектов',
            description:
              'Рефорс для бизнеса: охранная сигнализация, тревожная кнопка и пульт реагирования для офисов, магазинов и складов. Готовые пакеты, монтаж под ключ и контроль 24/7.',
            image: 'assets/images/backgrounds/for-business.jpg',
          },
        },
        loadComponent: () =>
          import('./pages/for-business/for-business.component').then(
            ({ ForBusinessComponent }) => ForBusinessComponent,
          ),
      },
      {
        path: 'partner',
        data: {
          seo: {
            title: 'Партнёрская программа',
            description:
              'Зарабатывайте с Рефорс: подключайте клиентов к охранным сервисам и получайте стабильный доход. Поддержка, поток заявок и готовая платформа для партнёров.',
            image: 'assets/images/backgrounds/for-partners.jpg',
          },
        },
        loadComponent: () =>
          import('./pages/for-partners/for-partners.component').then(
            ({ ForPartnersComponent }) => ForPartnersComponent,
          ),
      },
      {
        path: 'career',
        data: {
          seo: {
            title: 'Карьера в Рефорс',
            description:
              'Работа в Рефорс: создаём безопасное завтра вместе. Открытые направления, прозрачный процесс отбора и команда, которой можно гордиться. Присоединяйтесь — отправьте резюме.',
            image: 'assets/images/backgrounds/career.jpg',
          },
        },
        loadComponent: () =>
          import('./pages/career/career.component').then(({ CareerComponent }) => CareerComponent),
      },
      {
        path: 'contacts',
        data: {
          seo: {
            title: 'Контакты Рефорс',
            description: 'Контакты Рефорс: телефон, почта и поддержка 24/7.',
          },
        },
        loadComponent: () =>
          import('./pages/contacts/contacts.component').then(
            ({ ContactsComponent }) => ContactsComponent,
          ),
      },
      {
        path: 'katalog',
        children: [
          // /katalog — весь каталог; /katalog/:category — каталог, отфильтрованный
          // по типу товара (package/device/hardware/service).
          {
            path: '',
            data: {
              seo: {
                title: 'Каталог решений безопасности',
                description:
                  'Каталог Рефорс: готовые пакеты охраны квартир, домов и бизнеса, датчики и сервисы безопасности. Выберите решение и подключитесь за 24 часа.',
                image: 'assets/images/backgrounds/main.jpg',
              },
            },
            loadComponent: () =>
              import('./pages/catalog/catalog.component').then(
                ({ CatalogComponent }) => CatalogComponent,
              ),
          },
          {
            path: ':category',
            data: {
              seo: {
                title: 'Каталог решений безопасности',
                description:
                  'Каталог Рефорс: готовые пакеты охраны квартир, домов и бизнеса, датчики и сервисы безопасности. Выберите решение и подключитесь за 24 часа.',
                image: 'assets/images/backgrounds/main.jpg',
              },
            },
            loadComponent: () =>
              import('./pages/catalog/catalog.component').then(
                ({ CatalogComponent }) => CatalogComponent,
              ),
          },
          // Комплект: /katalog/komplekty/:serviceSlug/:configurationSlug/:id.
          // Остальные товары: /katalog/:type/:slug/:id.
          {
            matcher: canonicalProductCardMatcher,
            data: {
              seo: {
                title: 'Карточка товара',
                description: 'Подробная информация о товаре',
                image: 'assets/images/backgrounds/main.jpg',
              },
            },
            loadComponent: () =>
              import('./pages/product-card/product-card.component').then(
                ({ ProductCardComponent }) => ProductCardComponent,
              ),
          },
        ],
      },
      {
        path: 'privacy',
        data: {
          seo: {
            title: 'Политика конфиденциальности',
            description: 'Политика в отношении обработки персональных данных Рефорс',
            noindex: true,
          },
        },
        loadComponent: () =>
          import('./pages/privacy/privacy.component').then(
            ({ PrivacyComponent }) => PrivacyComponent,
          ),
      },
    ],
  },
  {
    path: 'checkout',
    data: {
      seo: {
        title: 'Оформление заявки',
        description: 'Оформление заявки Рефорс',
        noindex: true,
      },
    },
    loadComponent: () =>
      import('./pages/order-placing/order-placing.component').then(
        ({ OrderPlacingComponent }) => OrderPlacingComponent,
      ),
  },
  // Старый URL оформления — редиректим на /checkout ради сохранённых ссылок.
  { path: 'order-placing', redirectTo: 'checkout', pathMatch: 'full' },
  {
    path: '**',
    redirectTo: '',
  },
];
